import { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "@reach/router";

type SearchCallback<T> = (values: T) => void | Promise<void>

export const usePersistedSearch = <T extends Record<string, unknown>>(
  initialValues: T,
  onSearchCallback: SearchCallback<T>,
): [T, SearchCallback<T>] => {
  const location = useLocation();
  const navigate = useNavigate();

  const query = new URLSearchParams(location.search);

  const formValues = useMemo(() => Object.entries(initialValues)
    .reduce(
      (memo, [key, initialValue]) => {
        const queryValue = query.get(key) ?? initialValue;
        const valueType = typeof initialValue;
        let castValue = queryValue;
        if (valueType === "boolean") {
          castValue = typeof queryValue === "string" ? queryValue === "true" : queryValue;
        }

        return ({
          ...memo,
          [key]: castValue,
        });
      },
      {},
    ) as T, [initialValues]);

  const search = async (variables: T) => {
    const params = new URLSearchParams({ ...variables } as Record<string, string>);
    await navigate(`?${params.toString()}`, { replace: true });

    return onSearchCallback(variables);
  };

  useEffect(() => {
    if (!location.search) return;

    onSearchCallback(formValues);
  }, []);

  return [formValues, search];
};
